import * as React from 'react';
import { requestItemsFor } from '@ps/api-client/applicant';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Checkbox, Field } from '@ps/ui-kit/components/field';
import { Input } from '@ps/ui-kit/components/input';

// The standard request list as staff use it (email specification §5, Annex A; ADR-096): tick items from the list;
// wording of their own only after "Add your own wording" and a second confirmation that shows it as the customer will
// see it. Shared by the admin request (applications) and the console request (held payments). The server refuses an
// item that is not on the list unless the request says it was confirmed.

export function useRequestItems() {
  const [items, setItems] = React.useState<string[]>([]);
  const [writing, setWriting] = React.useState(false);
  const [other, setOther] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const own = writing ? other.trim() : '';
  return {
    items, writing, other, own, confirming, setConfirming, setOther,
    all: [...items, ...(own ? [own] : [])],
    toggle: (i: string, on: boolean) => setItems(on ? [...items, i] : items.filter(x => x !== i)),
    startWriting: () => setWriting(true),
    /** First press with own wording: show the confirmation. Returns true when the request may be sent. */
    readyToSend: () => { if (own && !confirming) { setConfirming(true); return false; } return true; },
    /** What the request carries besides the items. */
    confirmation: () => (own ? { freeTextConfirmed: true as const } : {}),
  };
}
export type RequestItemsState = ReturnType<typeof useRequestItems>;

/** The list for an application or a held payment, then "Add your own wording". */
export function RequestItemsPicker({ s, where, idPrefix, children }: { s: RequestItemsState; where: 'APPLICATION' | 'PAYMENT'; idPrefix: string; children?: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="text-[13px] font-semibold text-ink-2">{where === 'APPLICATION' ? 'Missing items' : 'Items needed'}</legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {requestItemsFor(where).map(({ label }) => <Checkbox key={label} label={label} checked={s.items.includes(label)} onChange={e => s.toggle(label, e.target.checked)} />)}
      </div>
      {children}
      {s.writing
        ? <Field id={`${idPrefix}-other`} label="Your own wording" hint="Only if nothing on the list fits. The customer sees it exactly as written; you will be asked to confirm it." className="mt-3">
            <Input id={`${idPrefix}-other`} value={s.other} onChange={e => s.setOther(e.target.value)} autoFocus />
          </Field>
        : <Button type="button" variant="ghost" className="mt-3" onClick={s.startWriting}>Add your own wording</Button>}
    </fieldset>
  );
}

/** The second confirmation, in place of the form. */
export function OwnWordingConfirm({ text }: { text: string }) {
  return (
    <Alert tone="warn" title="You wrote this item yourself">
      <p className="mt-1 text-ink">“{text}”</p>
      <p className="mt-2 text-ink-2">The customer sees it exactly as written. Check it is neutral before you send it.</p>
    </Alert>
  );
}
